import { createSessionKeyedReadCache } from '../../runtime/session-read-cache';
import {
  normalizeInspectionDashboardPeriod,
  inspectionDashboardPeriodKey,
  type InspectionDashboardPeriod,
} from '../inspection-read-model/dashboard-period';

export type SiteInspectionSummary = {
  siteId: string;
  period: { from: string; to: string };
  inspections: number;
  findings: number;
  unresolved: number;
  approval: number;
  completed: number;
  reassign: number;
};
const cache = createSessionKeyedReadCache<{ value: SiteInspectionSummary; at: number }>(8);
export function parseSiteInspectionSummary(
  raw: unknown,
  period: { from: string; to: string },
  siteId: string,
): SiteInspectionSummary {
  const value = raw as Record<string, unknown> | null;
  const range = value?.period as Record<string, unknown> | undefined;
  if (
    !value ||
    value.siteId !== siteId ||
    value.scope !== 'site' ||
    value.contractorId !== null ||
    value.complete !== true ||
    range?.start !== period.from ||
    range.end !== period.to
  )
    throw new Error('SITE_SUMMARY_INCOMPLETE');
  const operations = value.inspectionOperations as Record<string, unknown> | undefined;
  const counts = value.workflowStatusCounts as Record<string, unknown> | undefined;
  const count = (value: unknown) => {
    if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0)
      throw new Error('SITE_SUMMARY_COUNT_INVALID');
    return value;
  };
  return {
    siteId,
    period,
    inspections: count(operations?.inspectionCount),
    findings: count(operations?.findingCount),
    unresolved: count(counts?.unresolved),
    approval: count(counts?.approval),
    completed: count(counts?.complete),
    reassign: count(counts?.reassign),
  };
}
export async function loadSiteInspectionSummary(
  uid: string,
  siteId: string,
  requested: InspectionDashboardPeriod,
  force = false,
) {
  const period = normalizeInspectionDashboardPeriod(requested);
  if ('mode' in period || !uid || !/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u.test(siteId) || import.meta.env.VITE_FIREBASE_PROJECT_ID !== 'kop-safety-51efb')
    throw new Error('SITE_SUMMARY_UNAVAILABLE');
  const key = `${uid}:${siteId}:${inspectionDashboardPeriodKey(period)}`;
  const cached = cache.peek(key);
  if (!force && cached && Date.now() - cached.at < 60_000) return cached.value;
  return (
    await cache.read(
      key,
      async () => {
        const module = await import('@kop/inspection-firebase-adapter');
        const raw = await module
          .createInspectionDashboardAnalyticsReadPort('kop-safety-51efb')
          .load({ tenantId: 'kop', siteId, period, fresh: force });
        return { value: parseSiteInspectionSummary(raw, period, siteId), at: Date.now() };
      },
      true,
    )
  ).value;
}
